import { format } from 'date-fns';
import { call, select } from 'redux-saga/effects';

import i18n, { DisplayLanguage } from '@app/services/translations';
import { getDateFormat } from '@app/services/translations/formats';
import { selectConfig } from '@app/redux/config';

import {
  BadgeStatus,
  Invoice,
  InvoiceStatus,
  Locale,
  NotificationLevel,
  ProductInstanceStatus,
} from '@mfe/shared/schema-types';

import { selectHasFiber, selectUser } from '../auth';
import { selectBillingInfo } from '../billingInfo';
import { ContractInstance, selectContractInstance } from '../electronicCustomerAgreement';
import { selectLocale } from '../locale';
import { selectCurrentUsage, selectPlanCharacteristics } from '../plan';
import {
  selectFiberServiceableMessage,
  selectIsFiberServiceable,
  selectScheduleInstallationError,
  waitForScheduleInstallationDates,
} from '../scheduleInstallation';
import { selectWorkOrders } from '../workOrders';
import { Alert, AlertAction, AlertType, DisplayLocation } from './alertsSlice';
import { parseDateStringWithoutTime } from '../../utils';

export function* parseAccountAlerts() {
  const {
    user: { productInstanceStatus },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const alerts: Alert[] = [];
  let activeAlerts: Alert | undefined;

  yield call(handleEcaAlertDisplay, alerts);

  switch (productInstanceStatus) {
    case ProductInstanceStatus.Active:
      activeAlerts = yield call(getActiveAlerts);
      if (activeAlerts) {
        alerts.push(activeAlerts);
      }
      break;
    case ProductInstanceStatus.Deactivated:
      alerts.push(yield call(getDeactivatedAlerts));
      break;
    case ProductInstanceStatus.Canceled:
      alerts.push(yield call(getCancelledAlerts));
      break;
    case ProductInstanceStatus.Suspended:
      alerts.push(yield call(getSuspendedAlerts));
      break;
    case ProductInstanceStatus.Preinstall:
      alerts.push(yield call(getPreinstallAlerts));
      break;
    default:
      break;
  }

  return alerts;
}

function* handleEcaAlertDisplay(alerts: Alert[]) {
  const contractInstance: ContractInstance = yield select(selectContractInstance);
  const signedStatus = contractInstance?.signedStatus;
  const enableFiber: boolean = yield select(selectConfig);
  if (signedStatus === false && enableFiber) {
    alerts.push({
      level: NotificationLevel.Info,
      title: i18n.t('Alerts:ElectronicCustomerAgreement.Required.title'),
      caption: i18n.t('Alerts:ElectronicCustomerAgreement.Required.caption'),
      button: {
        action: AlertAction.SignElectronicCustomerAgreement,
        label: i18n.t('Alerts:ElectronicCustomerAgreement.Required.button.text'),
      },
      type: AlertType.ElectronicCustomerAgreement,
      displayLocation: DisplayLocation.GLOBAL,
    });
  }
}

export const determineIsPreSoftSuspend = (invoices: Invoice[], isSoftSuspended: boolean) => {
  if (invoices.length === 0) {
    return false;
  }

  const unpaidInvoice = invoices.find((invoice) => invoice.invoiceStatus === InvoiceStatus.Unpaid);

  return Boolean(unpaidInvoice) && !isSoftSuspended;
};

function* getActiveAlerts() {
  const {
    user: { isSoftSuspended },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const isUS = userLocale === Locale.EnUs;
  const isBrazil = userLocale === Locale.PtBr;
  const isItaly = userLocale === Locale.ItIt;

  const {
    billingInfo: { summaryInvoiceStatus, invoices },
  }: ReturnType<typeof selectBillingInfo> = yield select(selectBillingInfo);
  const hasPastDueInvoices = summaryInvoiceStatus === BadgeStatus.PastDue;

  const isPreSoftSuspended = determineIsPreSoftSuspend(invoices, isSoftSuspended);

  const {
    characteristics: { hasUnlimitedUsageMeter },
  }: ReturnType<typeof selectPlanCharacteristics> = yield select(selectPlanCharacteristics);

  const {
    data: { hasBreachedDataAllowance, endDate, monthlyDataCap },
    errors: usageErrors,
  }: ReturnType<typeof selectCurrentUsage> = yield select(selectCurrentUsage);

  const allDataUsed = hasBreachedDataAllowance;
  const usageResetDate = endDate ? parseDateStringWithoutTime(endDate) : '';

  const hasBuyMoreDataButton = isItaly
    ? null
    : {
        action: AlertAction.BuyMore,
        label: i18n.t('Alerts:AccountStatus.dataAllUsed.button.text'),
      };

  if (isUS && hasPastDueInvoices) {
    return {
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:AccountStatus.pastDue.title'),
      caption: i18n.t('Alerts:AccountStatus.pastDue.caption'),
      button: {
        action: AlertAction.GoToBilling,
        label: i18n.t('Alerts:AccountStatus.pastDue.button.text'),
      },
      type: AlertType.PastDueInvoices,
      displayLocation: DisplayLocation.LOCAL,
    };
  }

  if (isBrazil && isSoftSuspended) {
    return {
      level: NotificationLevel.Error,
      title: i18n.t('Alerts:AccountStatus.softSuspend.title'),
      caption: i18n.t('Alerts:AccountStatus.softSuspend.caption'),
      button: {
        action: AlertAction.GoToBilling,
        label: i18n.t('Alerts:AccountStatus.softSuspend.button.text'),
      },
      type: AlertType.SoftSuspend,
      displayLocation: DisplayLocation.LOCAL,
    };
  }

  if (isBrazil && isPreSoftSuspended) {
    return {
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:AccountStatus.preSoftSuspend.title'),
      caption: i18n.t('Alerts:AccountStatus.preSoftSuspend.caption'),
      button: {
        action: AlertAction.GoToBilling,
        label: i18n.t('Alerts:AccountStatus.preSoftSuspend.button.text'),
      },
      type: AlertType.PreSoftSuspend,
      displayLocation: DisplayLocation.LOCAL,
    };
  }

  if (!usageErrors && allDataUsed && hasUnlimitedUsageMeter && usageResetDate && monthlyDataCap) {
    return {
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:AccountStatus.allDataUsedNextGen.title'),
      caption: i18n.t('Alerts:AccountStatus.allDataUsedNextGen.caption', {
        date: usageResetDate,
        amount: monthlyDataCap,
      }),
      button: {
        action: AlertAction.BuyMore,
        label: i18n.t('Alerts:AccountStatus.allDataUsedNextGen.button.text'),
      },
      link: {
        action: AlertAction.PlanUsage,
        label: i18n.t('Alerts:AccountStatus.allDataUsedNextGen.link.text'),
      },
      type: AlertType.AllDataUsedNextGen,
      displayLocation: DisplayLocation.LOCAL,
    };
  }

  if (!usageErrors && allDataUsed && !isSoftSuspended) {
    return {
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:AccountStatus.dataAllUsed.title'),
      caption: i18n.t('Alerts:AccountStatus.dataAllUsed.caption'),
      button: hasBuyMoreDataButton,
      link: {
        action: AlertAction.PlanUsage,
        label: i18n.t('Alerts:AccountStatus.dataAllUsed.link.text'),
      },
      type: AlertType.AllDataUsed,
      displayLocation: DisplayLocation.LOCAL,
    };
  }

  return;
}

function getCancelledAlerts() {
  // Cancelled product instance is a result of cancelling in flight
  // The account was never active and has no balance to pay
  return {
    level: NotificationLevel.Error,
    title: i18n.t('Alerts:AccountStatus.disconnected.title'),
    caption: i18n.t('Alerts:AccountStatus.disconnected.caption'),
    type: AlertType.CancelledAccount,
    displayLocation: DisplayLocation.LOCAL,
  };
}

function* getDeactivatedAlerts() {
  const {
    locale: { displayLanguage },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);
  const {
    billingInfo: { totalBalance },
  } = yield select(selectBillingInfo);
  const isBrazil = displayLanguage === DisplayLanguage.Portuguese;
  const shouldShowPayButton = totalBalance.value > 0;

  return {
    level: NotificationLevel.Error,
    title: i18n.t('Alerts:AccountStatus.disconnected.title'),
    caption: i18n.t('Alerts:AccountStatus.disconnected.caption'),
    button: shouldShowPayButton && {
      action: AlertAction.GoToBilling,
      label: i18n.t('Alerts:AccountStatus.disconnected.button.text'),
    },
    link: isBrazil
      ? {
          label: i18n.t('Alerts:AccountStatus.disconnected.link.text'),
          url: i18n.t('Alerts:AccountStatus.disconnected.link.url'),
        }
      : undefined,
    type: AlertType.DisconnectedAccount,
    displayLocation: DisplayLocation.LOCAL,
  };
}

function getSuspendedAlerts() {
  return {
    level: NotificationLevel.Error,
    title: i18n.t(`Alerts:AccountStatus.suspended.title`),
    caption: i18n.t(`Alerts:AccountStatus.suspended.caption`),
    button: {
      action: AlertAction.GoToBilling,
      label: i18n.t(`Alerts:AccountStatus.suspended.button.text`),
    },
    type: AlertType.SuspendedAccount,
    displayLocation: DisplayLocation.LOCAL,
  };
}

function* getPreinstallAlerts() {
  yield call(waitForScheduleInstallationDates);

  const { installDate, schedule } = yield select(selectWorkOrders);
  const hasFiberInternet: boolean = yield select(selectHasFiber);
  const isServiceable: boolean | null = yield select(selectIsFiberServiceable);
  const message: string = yield select(selectFiberServiceableMessage);
  const error: string | null = yield select(selectScheduleInstallationError);
  const {
    locale: { userLocale },
  }: ReturnType<typeof selectLocale> = yield select(selectLocale);

  const showScheduleInstallation = userLocale === Locale.EnUs;

  const nonServiceableAlert: Alert = {
    level: NotificationLevel.Info,
    title: i18n.t('Alerts:AccountStatus.nonServiceable.title'),
    caption: i18n.t('Alerts:AccountStatus.nonServiceable.caption', {
      message: message || i18n.t('Alerts:AccountStatus.nonServiceable.defaultMessage'),
    }),
    type: AlertType.NonServiceable,
    displayLocation: DisplayLocation.LOCAL,
  };

  const unscheduledAlert: Alert = {
    level: NotificationLevel.Info,
    title: i18n.t('Alerts:AccountStatus.unscheduled.title'),
    caption: i18n.t('Alerts:AccountStatus.unscheduled.caption'),
    button: showScheduleInstallation
      ? {
          action: AlertAction.ScheduleInstall,
          label: i18n.t('Alerts:AccountStatus.unscheduled.button.text'),
        }
      : undefined,
    type: AlertType.UnscheduledInstallation,
    displayLocation: DisplayLocation.GLOBAL,
  };

  if (error) {
    return {
      level: NotificationLevel.Error,
      title: i18n.t('Alerts:AccountStatus.nonServiceable.title'),
      caption: i18n.t('Alerts:AccountStatus.nonServiceable.error'),
    };
  }

  const isFiberNonServiceable = hasFiberInternet && !isServiceable;
  const isFiberUnscheduled = hasFiberInternet && isServiceable && !installDate;
  const isSatelliteUnscheduled = !hasFiberInternet && !installDate;

  if (isFiberNonServiceable) {
    return nonServiceableAlert;
  }

  if (isFiberUnscheduled || isSatelliteUnscheduled) {
    return unscheduledAlert;
  }

  const formattedDate = format(new Date(installDate), getDateFormat(Locale.EnUs, 'medium_install_precise'));

  const from = new Date(schedule.from);
  const to = new Date(schedule.to);

  const scheduledAlert: Alert = {
    level: NotificationLevel.Info,
    title: i18n.t('Alerts:AccountStatus.scheduled.title'),
    caption: i18n.t('Alerts:AccountStatus.scheduled.caption', {
      formattedDate,
      from,
      to,
    }),
    button: showScheduleInstallation
      ? {
          action: AlertAction.ScheduleInstall,
          label: i18n.t('Alerts:AccountStatus.scheduled.button.text'),
        }
      : undefined,
    type: AlertType.ScheduledInstallation,
    displayLocation: DisplayLocation.LOCAL,
  };

  return scheduledAlert;
}
